import { FunctionComponent, useState, useEffect, useRef, useMemo } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import ProductsHeader from './Header/ProductsHeader';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProductsThunk } from '@spike/products-action';
import { ProductsState } from '@spike/products-action';
import { RootState } from 'store';
import { fetchThunk as fetchVendorsThunk } from 'actions/productVendors/ProductVendorsActions';
import { fetchThunk as fetchCategoriesThunk } from 'actions/productCategories/ProductCategoriesActions';
import { ProductVendorsState } from 'reducers/productVendors/ProductVendorsState';
import { ProductCategoriesState } from 'reducers/productCategories/ProductCategoriesState';
import Table from './Table/Table';
import { Product } from '@spike/product-model';
import Pagination from 'components/UI/Pagination';
import ProductVendor from 'model/ProductVendor';
import ProductCategory from 'model/ProductCategory';
import ProductComponent from './Product/ProductComponent';
import VendorComponent from './Vendor/VendorComponent';
import CategoryComponent from './Category';
import AssignProductsComponent from './Category/AssignProductsComponent';
import { useApiClientWrapper } from 'hooks';
import Stock from './Stock';
import Dashboard from './Dashboard';
import OnboardingTutorial from 'components/OnboardingTutorial';
import { productSlides } from './Slides';
import { useHistory } from 'react-router-dom';
import { ProductsPath } from 'routes/Paths';

export type ProductsViewId = 'dashboard' | 'products' | 'vendors' | 'categories' | 'stock';

const EMPTY_CATEGORY_IDS: Array<number> = [];

interface Props {
    selectedViewId: ProductsViewId;
    sidebarSelectionKey?: number;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            width: '100%',
            height: '100%',
            position: 'relative',
            borderRadius: '18px',
            background: '#FAFAFA'
        },
        headerContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            background: '#FAFAFA'
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden'
        },
        pagination: {
            flexShrink: 0,
            width: '100%',
            borderTop: '1px solid #DDDDDD',
            paddingTop: '8px !important',
            paddingBottom: '8px !important',
            background: '#FAFAFA',
            [theme.breakpoints.down(768)]: {
                paddingLeft: '16px !important',
                paddingRight: '16px !important'
            },
            [theme.breakpoints.between(768, 'sm')]: {
                paddingLeft: '27px !important',
                paddingRight: '40px !important'
            },
            [theme.breakpoints.only('md')]: {
                paddingLeft: '43px !important',
                paddingRight: '65px !important'
            },
            [theme.breakpoints.only('lg')]: {
                paddingLeft: '57px !important',
                paddingRight: '87px !important'
            },
            [theme.breakpoints.only('xl')]: {
                paddingLeft: '87px !important',
                paddingRight: '132px !important'
            }
        },
        tableContainer: {
            width: '100%',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            background: '#FAFAFA',
            [theme.breakpoints.down('md')]: {
                paddingTop: '26px'
            },
            [theme.breakpoints.up('lg')]: {
                paddingTop: '32px'
            }
        },
        tableBody: {
            flex: 1,
            minHeight: 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'stretch',
            overflow: 'hidden',
            background: '#FFFFFF'
        },
        onboardingContainer: {
            height: '100%',
            width: '100%'
        },
        spinnerStyle: {
            height: '100%'
        }
    })
);

export const ProductsComponent: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const [searchText, setSearchText] = useState('');
    const searchTextRef = useRef(searchText);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const history = useHistory();
    const [showNewProduct, setShowNewProduct] = useState(false);
    const [returnToStock, setReturnToStock] = useState(false);
    const [showNewVendor, setShowNewVendor] = useState(false);
    const [showNeworRenameCategory, setShowNeworRenameCategory] = useState(false);
    const [vendorsActive, setVendorsActive] = useState(true);
    const [vendorsDeactive, setVendorsDeactive] = useState(true);
    const [productCategoryFilter, setProductCategoryFilter] = useState<(number | undefined)[]>([undefined]);
    const [productCategoryAllFilter, setProductCategoryAllFilter] = useState(false);
    const [productEdit, setProductEdit] = useState<Product | undefined>(undefined);
    const [productsCategoryFiltered, setProductsCategoryFiltered] = useState(false);
    const [vendorEdit, setVendorEdit] = useState<ProductVendor | undefined>(undefined);
    const [categoryToRename, setCategoryToRename] = useState<ProductCategory | undefined>(undefined);
    const [showAssignProducts, setShowAssignProducts] = useState(false);
    const [redirectionVendor, setRedirectionVendor] = useState(false);
    const [redirectionCategory, setRedirectionCategory] = useState(false);
    const [vendorsViewLoading, setVendorsViewLoading] = useState(false);

    const products = useSelector<RootState, ProductsState>(state => state.products);
    const vendors = useSelector<RootState, ProductVendorsState>(state => state.productVendors);
    const categories = useSelector<RootState, ProductCategoriesState>(state => state.productCategories);
    const loadingProducts = useSelector<RootState, boolean>(state => state.products.loading);
    const loadingVendors = useSelector<RootState, boolean>(state => state.productVendors.loading);
    const loadingCategories = useSelector<RootState, boolean>(state => state.productCategories.loading);

    let vendorEditRedirection: ProductVendor | undefined = undefined;

    useEffect(() => {
        setPage(1);
        setPageSize(10);
        setSearchText('');
        searchTextRef.current = '';
        setProductCategoryFilter([undefined]);
        setProductCategoryAllFilter(false);
        setProductsCategoryFiltered(false);
        setShowNewProduct(false);
        setShowNewVendor(false);
        setShowNeworRenameCategory(false);
        setShowAssignProducts(false);
        setProductEdit(undefined);
        setVendorEdit(undefined);
        setCategoryToRename(undefined);
        setRedirectionVendor(false);
        setRedirectionCategory(false);
        setVendorsViewLoading(props.selectedViewId === 'vendors');
    }, [props.selectedViewId, props.sidebarSelectionKey]);

    const categoryIds = useMemo(() => {
        if (!productsCategoryFiltered || productCategoryAllFilter) {
            return EMPTY_CATEGORY_IDS;
        }

        const selectedIds = productCategoryFilter.filter((id): id is number => id !== undefined);
        return selectedIds.length > 0 ? selectedIds : EMPTY_CATEGORY_IDS;
    }, [productCategoryFilter, productCategoryAllFilter, productsCategoryFiltered]);

    useEffect(() => {
        if (props.selectedViewId !== 'products') {
            return;
        }
        dispatch(fetchVendorsThunk());
        dispatch(fetchCategoriesThunk());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedViewId, props.sidebarSelectionKey]);

    useEffect(() => {
        if (props.selectedViewId !== 'products') {
            return;
        }
        dispatch(fetchProductsThunk(apiClientWrapper, searchTextRef.current, [], [], categoryIds, page, pageSize));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedViewId, props.sidebarSelectionKey, page, pageSize, categoryIds]);

    useEffect(() => {
        if (props.selectedViewId !== 'vendors') {
            return;
        }

        let cancelled = false;

        const loadVendorsView = async () => {
            setVendorsViewLoading(true);
            try {
                await dispatch(
                    fetchProductsThunk(apiClientWrapper, '', [], [apiClientWrapper.marketplaceId], [], 1, 10000)
                );
                if (cancelled) {
                    return;
                }
                await Promise.all([dispatch(fetchVendorsThunk()), dispatch(fetchCategoriesThunk())]);
            } finally {
                if (!cancelled) {
                    setVendorsViewLoading(false);
                }
            }
        };

        loadVendorsView();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedViewId, props.sidebarSelectionKey]);

    useEffect(() => {
        if (props.selectedViewId !== 'categories') {
            return;
        }
        dispatch(fetchCategoriesThunk());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedViewId, props.sidebarSelectionKey]);

    const addProductHandler = () => {
        setReturnToStock(false);
        setProductEdit(undefined);
        setShowNewProduct(true);
        setRedirectionVendor(false);
        setRedirectionCategory(false);
    };

    const addProductFromStockHandler = () => {
        setProductEdit(undefined);
        setShowNewProduct(true);
        setRedirectionVendor(false);
        setRedirectionCategory(false);
        setReturnToStock(true);
    };

    const addVendorHandler = () => {
        setVendorEdit(undefined);
        setShowNewVendor(true);
        setRedirectionVendor(false);
    };

    const addCategoryHandler = () => {
        setCategoryToRename(undefined);
        setShowNeworRenameCategory(true);
        setRedirectionCategory(false);
    };

    const getProductList = () => {
        return products.list.page ?? [];
    };

    const getVendorList = () => {
        let vendorListFiltered: Array<ProductVendor> = vendors.list;

        if (searchText) {
            const vendorListAux = vendors.list.filter(item => {
                const name = item.name.toLowerCase();
                const contact = item.contact ? item.contact.toLowerCase() : '';
                const status = item.active ? 'active' : 'inactive';
                const email = item.email ? item.email.toLowerCase() : '';
                const website = item.website ? item.website.toLowerCase() : '';
                const state = item.address && item.address.state ? item.address.state.toLowerCase() : '';
                return (
                    searchText.length === 0 ||
                    name.indexOf(searchText.toLowerCase()) > -1 ||
                    contact.indexOf(searchText.toLowerCase()) > -1 ||
                    status.indexOf(searchText.toLowerCase()) > -1 ||
                    email.indexOf(searchText.toLowerCase()) > -1 ||
                    website.indexOf(searchText.toLowerCase()) > -1 ||
                    state.indexOf(searchText.toLowerCase()) > -1
                );
            });
            vendorListFiltered = vendorListAux;
        }

        if (!vendorsActive && vendorsDeactive) {
            vendorListFiltered = vendorListFiltered.filter(v => !v.active);
        }

        if (vendorsActive && !vendorsDeactive) {
            vendorListFiltered = vendorListFiltered.filter(v => v.active);
        }

        if (!vendorsActive && !vendorsDeactive) {
            vendorListFiltered = [];
        }

        return vendorListFiltered;
    };

    const getCategoryList = () => {
        let categoryListFiltered: Array<ProductCategory> = [];

        if (searchText) {
            const categoryListAux = categories.list.filter(item => {
                const name = `${item.name.toLowerCase()}`;
                const status = item.active ? 'active' : 'inactive';
                return (
                    searchText.length === 0 ||
                    name.indexOf(searchText.toLowerCase()) > -1 ||
                    status.indexOf(searchText.toLowerCase()) > -1
                );
            });
            categoryListFiltered = categoryListAux;
        } else {
            categoryListFiltered = categories.list;
        }

        return categoryListFiltered;
    };

    const getLoading = () => {
        return props.selectedViewId === 'products'
            ? loadingProducts
            : props.selectedViewId === 'vendors'
            ? vendorsViewLoading
            : loadingCategories;
    };

    const getVisiblePagination = () => {
        return props.selectedViewId === 'products' && (products.list.pagination?.totalRecords ?? 0) > 0
            ? true
            : props.selectedViewId === 'vendors' && vendors.list.length > 0
            ? true
            : props.selectedViewId === 'categories' && categories.list.length > 0
            ? true
            : false;
    };

    const changePageHandler = (nextPage: number) => {
        setPage(nextPage);
    };

    const changePageSizeHandler = (nextPageSize: number) => {
        setPage(1);
        setPageSize(nextPageSize);
    };

    const handleSearch = (text: string) => {
        if (text === searchTextRef.current) {
            return;
        }

        searchTextRef.current = text;
        setSearchText(text);

        if (props.selectedViewId !== 'products') {
            setPage(1);
            return;
        }

        if (page !== 1) {
            setPage(1);
            return;
        }

        dispatch(fetchProductsThunk(apiClientWrapper, text, [], [], categoryIds, 1, pageSize));
    };

    const handleSelectProductCategory = (ids: (number | undefined)[], all: boolean) => {
        setProductCategoryFilter(ids);
        setProductCategoryAllFilter(all);
        setProductsCategoryFiltered(true);
        setPage(1);
    };

    const handleSelectVendor = (active: boolean, deactive: boolean) => {
        setVendorsActive(active);
        setVendorsDeactive(deactive);
    };

    const handleBackProduct = () => {
        const shouldReturnToStock = returnToStock;
        setShowNewProduct(false);
        setProductEdit(undefined);
        setRedirectionVendor(false);
        setRedirectionCategory(false);
        setReturnToStock(false);

        if (shouldReturnToStock) {
            history.replace(`${ProductsPath}#stock`);
        }
    };

    const handleEditProduct = (productToEdit: Product) => {
        setReturnToStock(false);
        setRedirectionVendor(false);
        setRedirectionCategory(false);
        setProductEdit(productToEdit);
        setShowNewProduct(true);
    };

    const handleEditVendor = (vendorToEdit: ProductVendor) => {
        setShowNewVendor(true);
        setVendorEdit(vendorToEdit);
    };

    const handleEditCategory = (categoryToEdit: ProductCategory) => {
        setShowNeworRenameCategory(true);
        setCategoryToRename(categoryToEdit);
    };

    const handleAssignProducts = (categoryToEdit: ProductCategory) => {
        setShowNeworRenameCategory(false);
        setShowAssignProducts(true);
        setCategoryToRename(categoryToEdit);
    };

    const handleBackVendor = (redirect: boolean) => {
        setShowNewVendor(false);
        setVendorEdit(undefined);
        if (redirect) {
            setShowNewProduct(true);
        }
    };

    const handleBackCategory = () => {
        setShowNeworRenameCategory(false);
        setShowAssignProducts(false);
        setCategoryToRename(undefined);
    };

    const handleVendorSaved = (v: ProductVendor) => {
        const vendorSelected = vendors.list.find(vend => vend.uuid === v.uuid);
        setVendorEdit(vendorSelected);
        vendorEditRedirection = vendorSelected;
    };

    const handleAddVendor = (p: Product) => {
        setRedirectionVendor(true);
        setVendorEdit(undefined);
        setShowNewVendor(true);
        setShowNewProduct(false);
        setProductEdit(p);
    };

    const handleAddCategory = (p: Product) => {
        setRedirectionCategory(true);
        setCategoryToRename(undefined);
        setShowNeworRenameCategory(true);
        setProductEdit(p);
    };

    const handleRedirectionVendor = () => {
        setShowNewProduct(true);
        setShowNewVendor(false);
        setProductEdit((prevProduct: any) => {
            return {
                ...prevProduct,
                vendor: {
                    id: vendorEditRedirection?.id,
                    name: vendorEditRedirection?.name
                }
            };
        });
    };

    const handleCategorySaved = (c: ProductCategory) => {
        const categorySelected = categories.list.find(categ => categ.uuid === c.uuid);
        setCategoryToRename(categorySelected);
        setShowNeworRenameCategory(false);
        setProductEdit((prevProduct: any) => {
            return {
                ...prevProduct,
                category: {
                    id: categorySelected?.id,
                    name: categorySelected?.name
                }
            };
        });
    };

    const handleShowVendor = (vendorId: number, productoToEdit: Product) => {
        setShowNewVendor(true);
        setShowNewProduct(false);
        setRedirectionVendor(true);
        const vendorSelected = vendors.list.find(v => v.id === vendorId);
        setVendorEdit(vendorSelected);
        setProductEdit(productoToEdit);
    };

    const addNewProduct = (
        <ProductComponent
            key={productEdit?.uuid ?? productEdit?.id ?? 'new-product'}
            product={productEdit}
            redirection={redirectionVendor || redirectionCategory}
            onBack={handleBackProduct}
            onAddVendor={handleAddVendor}
            onAddCategory={handleAddCategory}
            onShowVendor={handleShowVendor}
        />
    );

    const addNewVendor = (
        <VendorComponent
            vendor={vendorEdit}
            redirection={redirectionVendor}
            onBack={handleBackVendor}
            vendorSaved={handleVendorSaved}
            onRedirection={handleRedirectionVendor}
        />
    );

    const addOrRenameCategory = (
        <CategoryComponent
            category={categoryToRename}
            onAssign={handleAssignProducts}
            categorySaved={handleCategorySaved}
            onClose={handleBackCategory}
        />
    );

    const assignProductsToCategory = (
        <AssignProductsComponent
            category={categoryToRename!}
            products={products.list.page ?? []}
            onClose={handleBackCategory}
        />
    );

    const pagination = (
        <Pagination
            className={classes.pagination}
            count={
                props.selectedViewId === 'products'
                    ? products.list.pagination?.totalRecords ?? 0
                    : props.selectedViewId === 'vendors'
                    ? getVendorList().length
                    : getCategoryList().length
            }
            page={page}
            pageSize={pageSize}
            onPageChange={changePageHandler}
            onPageSizeChange={changePageSizeHandler}
        />
    );

    const onboardingPageParams = {
        title: 'Products',
        buttonLabel: 'Add Product',
        imageWidth: 300,
        imageHeight: 500
    };

    const onBoarding = (
        <Box className={classes.onboardingContainer}>
            <OnboardingTutorial
                id="products_onboarding"
                titlePage={onboardingPageParams.title}
                addButtonLabel={onboardingPageParams.buttonLabel}
                onPressAdd={addProductHandler}
                imageWidth={onboardingPageParams.imageWidth}
                imageHeight={onboardingPageParams.imageHeight}
                slides={productSlides}
            />
        </Box>
    );

    return (
        <>
            <Box className={classes.container}>
                {/* {getLoading() && <Spinner className={classes.spinnerStyle} />} */}
                {!loadingProducts &&
                (products.list.pagination?.totalRecords ?? 0) === 0 &&
                !searchText &&
                !showNewProduct &&
                props.selectedViewId !== 'stock' &&
                props.selectedViewId !== 'dashboard' ? (
                    onBoarding
                ) : (
                    <Box className={classes.headerContainer}>
                        {showNewProduct ? (
                            addNewProduct
                        ) : showNewVendor ? (
                            addNewVendor
                        ) : (
                            <Box className={classes.content}>
                                {props.selectedViewId === 'dashboard' ? (
                                    <Dashboard key={props.sidebarSelectionKey} />
                                ) : props.selectedViewId === 'stock' ? (
                                    <Stock
                                        key={props.sidebarSelectionKey}
                                        onAddProduct={addProductFromStockHandler}
                                    />
                                ) : (
                                    <>
                                        <ProductsHeader
                                            selectedViewId={props.selectedViewId}
                                            onSearch={handleSearch}
                                            vendorsActive={vendorsActive}
                                            vendorsDeactive={vendorsDeactive}
                                            products={products.list.page ?? []}
                                            vendors={vendors.list}
                                            categories={categories.list}
                                            onAddProduct={addProductHandler}
                                            onAddVendor={addVendorHandler}
                                            onAddCategory={addCategoryHandler}
                                            onSelectProductCategory={handleSelectProductCategory}
                                            onSelectVendor={handleSelectVendor}
                                            loading={loadingProducts || loadingVendors || loadingCategories}
                                        />
                                        <Box className={classes.tableContainer}>
                                            <Box className={classes.tableBody}>
                                                <Table
                                                    selectedView={props.selectedViewId}
                                                    loading={getLoading()}
                                                    products={getProductList()}
                                                    vendors={
                                                        pageSize === 0
                                                            ? getVendorList()
                                                            : getVendorList().slice(
                                                                  (page - 1) * pageSize,
                                                                  page * pageSize
                                                              )
                                                    }
                                                    categories={
                                                        pageSize === 0
                                                            ? getCategoryList()
                                                            : getCategoryList().slice(
                                                                  (page - 1) * pageSize,
                                                                  page * pageSize
                                                              )
                                                    }
                                                    onEditProduct={handleEditProduct}
                                                    onEditVendor={handleEditVendor}
                                                    onEditCategory={handleEditCategory}
                                                    onAssign={handleAssignProducts}
                                                />
                                            </Box>
                                        </Box>
                                        {!getLoading() && getVisiblePagination() && pagination}
                                    </>
                                )}
                            </Box>
                        )}
                        {showNeworRenameCategory && addOrRenameCategory}
                        {showAssignProducts && assignProductsToCategory}
                    </Box>
                )}
            </Box>
        </>
    );
};

export default ProductsComponent;
